/**
 * Adjust Panel Component (canvas-v2)
 *
 * Provides speed and volume controls for the selected clip.
 * - Speed slider: 0.1x - 4.0x (step 0.1) with preset buttons (0.5x, 1.0x, 1.5x, 2.0x)
 * - Volume slider: 0%-200% display (snaps to 100% within +/-0.02 of slider
 *   midpoint — audio AND video clips). 100% = original level (tick mark +
 *   snap detent). Above 100% the slider maps dB-linearly so 200% = +24 dB
 *   (~15.85x amplitude) — see `volumeMapping.ts`. The stored `volume` field
 *   is linear amplitude, applied by the backend native ffmpeg `volume` filter
 *   on render and by a WebAudio GainNode on preview.
 * - Mute toggle (audio AND video clips)
 * - Clip info display
 * - Empty state: "Select a clip to adjust."
 */

import { memo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { AudioLines, Download, Scissors, Volume2, VolumeX } from 'lucide-react'
import type { CharacterVoiceCatalogueEntry } from '../../../../api/client'
import type { TimelineClip, TimelineAudioClip } from './types'
import { BasicClipControls } from './BasicClipControls'
import { SpeedCurveEditor } from './SpeedCurveEditor'
import type { SpeedPoint } from './speedCurve'
import { amplitudeToPosition, positionToAmplitude } from './volumeMapping'
import { createNadDebug } from '../../../../utils/debugLog'
import { useVoiceCatalogueModal } from '../../nodes/shared/VoiceCatalogueModalProvider'

const adjustLog = createNadDebug('dw:timeline-playback:render-contract')
// Feature-tagged logger for the 0-200% volume slider (RFC: timeline-volume).
// Logs raw vs snapped values plus the position→amplitude conversion so the
// trace shows both what the user dragged and the linear amplitude written.
const volumeLog = createNadDebug('dw:timeline-volume:adjust-panel')

/**
 * Snap-to-100% detent. A volume reading within +/-VOLUME_SNAP_BAND of 1.0
 * is treated as exactly 1.0 — matches the visual tick mark on the slider
 * so users can land on "original volume" without pixel-perfect dragging.
 */
const VOLUME_SNAP_BAND = 0.02

interface AdjustPanelProps {
  onSpeedCurve?: (clip: TimelineClip, points: SpeedPoint[]) => void
  onAudioFadesChange?: (id: string, fadeIn: number, fadeOut: number) => void
  onTimeEdit?: (clip: TimelineClip, mode: 'reverse' | 'freeze') => void
  timeEditBusy?: boolean
  timeEditError?: string | null
  playheadPosition?: number
  onClipVisualsChange?: (id: string, adjustments: NonNullable<TimelineClip['adjustments']>, fadeColor?: TimelineClip['fadeColor']) => void
  /** Currently selected video clip (null if audio or none or multi-selection) */
  selectedVideoClip: TimelineClip | null

  /** Currently selected audio clip (null if video or none or multi-selection) */
  selectedAudioClip: TimelineAudioClip | null

  /**
   * Type of selected clip — 'mixed' when video+audio are selected together
   * (multi-selection across types). Single-clip selection is the only state
   * where the panel renders editing controls.
   */
  selectedClipType: 'video' | 'audio' | 'mixed' | null

  /** True when the Original audio lane projects a video clip's native audio. */
  isNativeAudioSelection?: boolean

  /** Number of currently-selected clips (used to render the multi-selection state) */
  multiSelectionCount?: number

  /** Callback when clip speed changes */
  onSpeedChange: (clipId: string, speed: number) => void

  /** Callback when audio clip volume changes */
  onAudioVolumeChange: (clipId: string, volume: number) => void

  /** Callback when audio clip mute is toggled */
  onAudioToggleMute: (clipId: string) => void

  /** Callback when video clip volume changes */
  onClipVolumeChange: (clipId: string, volume: number) => void

  /** Callback when video clip mute is toggled */
  onClipToggleMute: (clipId: string) => void

  /**
   * Callback when video clip fade-out duration changes (seconds).
   * Video-only: see RFC-0501. Audio fades have a separate plumbing path.
   */
  onClipFadeOutChange: (clipId: string, fadeOutS: number) => void

  /**
   * Callback when video clip fade-in duration changes (seconds).
   * Symmetric to fade-out. Both render via the live preview overlay.
   */
  onClipFadeInChange: (clipId: string, fadeInS: number) => void

  /**
   * Download the SOURCE audio file for the selected audio clip. When present,
   * a download icon button is rendered next to the audio clip name in the
   * clip-info header. Downloads the underlying asset (`clip.sourcePath`) — the
   * timeline's trim/speed/volume/mute edits are NOT applied (see the tooltip
   * copy). Undefined → no button (e.g. for video clips). Owned by
   * `TimelineEditorModal`, which has the project context + blob preloader.
   */
  onDownloadAudioClip?: (clip: TimelineAudioClip) => void
  onExtractAudio?: () => void
  extractAudioDisabled?: boolean
  onChangeVoice?: (voice: CharacterVoiceCatalogueEntry) => void | Promise<void>
  onIsolateAudio?: () => void | Promise<void>
  onSplitDialogue?: () => void | Promise<void>
  audioActionsBusy?: boolean
  audioActionInFlight?: 'change_voice' | 'isolate_audio' | 'split_dialogue' | null
  audioActionError?: string | null
}

const SPEED_PRESETS = [0.5, 1.0, 1.5, 2.0]

export const AdjustPanel = memo(function AdjustPanel({
  selectedVideoClip,
  selectedAudioClip,
  selectedClipType,
  isNativeAudioSelection = false,
  multiSelectionCount = 0,
  onSpeedChange,
  onClipVisualsChange,
  onTimeEdit,
  timeEditBusy,
  timeEditError,
  playheadPosition,
  onAudioVolumeChange,
  onAudioToggleMute,
  onClipVolumeChange,
  onClipToggleMute,
  onClipFadeOutChange,
  onClipFadeInChange,
  onDownloadAudioClip,
  onExtractAudio,
  extractAudioDisabled = false,
  onChangeVoice,
  onIsolateAudio,
  onSplitDialogue,
  audioActionsBusy = false,
  audioActionInFlight = null,
  audioActionError = null,
  onSpeedCurve,
  onAudioFadesChange,
}: AdjustPanelProps) {
  const { t } = useTranslation('canvas')
  const clip = selectedClipType === 'video' ? selectedVideoClip : selectedClipType === 'audio' ? selectedAudioClip : null
  const videoOnlyClip = !isNativeAudioSelection && selectedClipType === 'video' ? selectedVideoClip : null

  // Audio-control surface — works for both audio AND video clips since both
  // now carry `volume` and `muted` fields.
  const audioControlClip = selectedClipType === 'audio' ? selectedAudioClip : selectedClipType === 'video' ? selectedVideoClip : null
  const audioControlClipType: 'video' | 'audio' | null =
    selectedClipType === 'video' || selectedClipType === 'audio' ? selectedClipType : null
  const { openVoiceCatalogueModal } = useVoiceCatalogueModal()
  const changeVoiceBusy = audioActionInFlight === 'change_voice'
  const isolateAudioBusy = audioActionInFlight === 'isolate_audio'
  const splitDialogueBusy = audioActionInFlight === 'split_dialogue'

  const handleSpeedSliderChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!clip) return
      const speed = parseFloat(e.target.value)
      onSpeedChange(clip.id, speed)
      // Intentionally NOT blurring the slider here: the modal keydown handler
      // already lets Space through for any target. Blurring on every onChange
      // breaks keyboard adjustment (Arrow/Page/Home/End) by stealing focus
      // after the first nudge.
    },
    [clip, onSpeedChange]
  )

  const handleSpeedPreset = useCallback(
    (speed: number) => {
      if (!clip) return
      onSpeedChange(clip.id, speed)
      // Blur the just-clicked preset button so a subsequent Space goes to the
      // modal's playback toggle instead of re-clicking the focused button.
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }
    },
    [clip, onSpeedChange]
  )

  const handleVolumeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!audioControlClip) return
      const rawPosition = parseFloat(e.target.value)
      // Defensive: parseFloat('') / parseFloat('abc') returns NaN. Without
      // this guard a NaN would propagate into the reducer and corrupt the
      // clip's persisted volume.
      if (Number.isNaN(rawPosition)) return
      // Snap-to-100% works in slider POSITION space (the tick is at 1.0).
      // After snap, convert position → amplitude before persisting; the
      // stored field is linear amplitude (applied by the ffmpeg `volume`
      // filter on render / WebAudio GainNode on preview).
      const snappedPosition =
        Math.abs(rawPosition - 1.0) <= VOLUME_SNAP_BAND ? 1.0 : rawPosition
      const amplitude = positionToAmplitude(snappedPosition)
      volumeLog({
        event: 'volume-change',
        clipId: audioControlClip.id,
        targetType: audioControlClipType,
        rawPosition,
        snappedPosition,
        amplitude,
        snapped_to_1: snappedPosition !== rawPosition,
      })
      if (audioControlClipType === 'audio') {
        onAudioVolumeChange(audioControlClip.id, amplitude)
      } else {
        onClipVolumeChange(audioControlClip.id, amplitude)
      }
      // Same reasoning as handleSpeedSliderChange: no blur, so keyboard
      // adjustments (Arrow/Page/Home/End) keep working.
    },
    [audioControlClip, audioControlClipType, onAudioVolumeChange, onClipVolumeChange]
  )

  const handleToggleMute = useCallback(() => {
    if (!audioControlClip) return
    if (audioControlClipType === 'audio') {
      onAudioToggleMute(audioControlClip.id)
    } else {
      onClipToggleMute(audioControlClip.id)
    }
    // Blur the mute button so Space toggles playback instead of re-clicking it.
    // Buttons are special-cased here because Space on a focused button =
    // synthetic click (browser default), which would re-toggle mute.
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }
    adjustLog({
      event: 'mute-toggle-blurred',
      clipId: audioControlClip.id,
      type: audioControlClipType,
    })
  }, [audioControlClip, audioControlClipType, onAudioToggleMute, onClipToggleMute])

  const handleChangeVoice = useCallback(
    async (voice: CharacterVoiceCatalogueEntry) => {
      if (!onChangeVoice) return
      await Promise.resolve(onChangeVoice(voice))
    },
    [onChangeVoice],
  )

  const openChangeVoiceModal = useCallback(() => {
    if (!audioControlClip || !onChangeVoice) return
    const targetLabel =
      ('label' in audioControlClip ? audioControlClip.label : undefined) || audioControlClip.id
    openVoiceCatalogueModal({
      kind: 'video_editor_change_voice',
      targetLabel,
      onApply: handleChangeVoice,
    })
  }, [audioControlClip, handleChangeVoice, onChangeVoice, openVoiceCatalogueModal])

  const handleIsolateAudio = useCallback(() => {
    if (!onIsolateAudio) return
    void Promise.resolve(onIsolateAudio())
  }, [onIsolateAudio])

  const handleFadeOutChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!selectedVideoClip) return
      const fadeOutS = parseFloat(e.target.value)
      if (Number.isNaN(fadeOutS)) return
      onClipFadeOutChange(selectedVideoClip.id, fadeOutS)
      adjustLog({
        event: 'fade-out-change',
        feature: 'videoeditor-fade-out',
        clipId: selectedVideoClip.id,
        fadeOutS,
      })
    },
    [selectedVideoClip, onClipFadeOutChange]
  )

  const handleFadeInChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!selectedVideoClip) return
      const fadeInS = parseFloat(e.target.value)
      if (Number.isNaN(fadeInS)) return
      onClipFadeInChange(selectedVideoClip.id, fadeInS)
      adjustLog({
        event: 'fade-in-change',
        feature: 'dw:video-editor-improvements',
        clipId: selectedVideoClip.id,
        fadeInS,
      })
    },
    [selectedVideoClip, onClipFadeInChange]
  )

  // Multi-selection / cross-type state: per-clip volume/speed editing isn't
  // defined for multi-clip; fall back to a hint instead of stale single-clip
  // controls. Use Ripple Delete or single-click to refocus.
  if (selectedClipType === 'mixed' || (multiSelectionCount > 1 && !clip)) {
    return (
      <div
        className="flex items-center justify-center h-full text-text-muted text-sm p-4 text-center"
        data-testid="timeline-adjust-multi-selection"
      >
        {t('adjustPanel.clipsSelected', { count: multiSelectionCount })}
        <br />
        {t('adjustPanel.selectSingleClip')}
      </div>
    )
  }

  // Empty state
  if (!clip) {
    return (
      <div className="flex items-center justify-center h-full text-text-muted text-sm p-4">
        {t('adjustPanel.selectClipToAdjust')}
      </div>
    )
  }

  const currentSpeed = clip.speed
  const audioPlannedEndOffsetS =
    selectedClipType === 'audio' &&
    selectedAudioClip &&
    typeof selectedAudioClip.plannedEndOffset === 'number' &&
    Number.isFinite(selectedAudioClip.plannedEndOffset)
      ? selectedAudioClip.plannedEndOffset
      : null
  const audioFitWindowS =
    audioPlannedEndOffsetS !== null && selectedAudioClip
      ? Math.max(0, audioPlannedEndOffsetS - selectedAudioClip.startOffset)
      : null
  const audioFitWindowRange =
    audioPlannedEndOffsetS !== null && selectedAudioClip
      ? t('adjustPanel.fitWindowRange', {
          start: selectedAudioClip.startOffset.toFixed(2),
          end: audioPlannedEndOffsetS.toFixed(2),
        })
      : null
  const sliderTestId =
    audioControlClipType === 'video' ? 'timeline-clip-volume-slider' : 'timeline-audio-volume-slider'
  const muteTestId =
    audioControlClipType === 'video' ? 'timeline-clip-mute-button' : 'timeline-audio-mute-button'

  return (
    <div className="flex h-full min-h-0 flex-col overflow-x-hidden">
      <div id="timeline-inspector-panel" className="flex min-w-0 flex-1 flex-col gap-4 overflow-y-auto overflow-x-hidden p-3">
      {/* Clip info */}
      <section className="min-w-0 border-b border-border pb-3">
        <div className="flex items-center justify-between gap-2">
          <div className="text-foreground text-sm font-medium truncate min-w-0 flex-1">
            {selectedClipType === 'video'
              ? selectedVideoClip?.label
              : selectedAudioClip?.label?.trim() || clip.sourcePath.split('/').pop() || t('adjustPanel.audioFallback')
            }
          </div>
          {/* Download source audio: only for a single audio clip with a
              non-empty sourcePath and a handler wired in. Downloads the raw
              source file (trim/speed/volume/mute NOT applied — see tooltip). */}
          {selectedClipType === 'audio' &&
            selectedAudioClip &&
            selectedAudioClip.sourcePath.trim() !== '' &&
            onDownloadAudioClip && (
              <button
                type="button"
                onClick={() => {
                  onDownloadAudioClip(selectedAudioClip)
                  // Blur so a subsequent Space routes to the modal's playback
                  // toggle rather than re-clicking this focused button — matches
                  // the mute / speed-preset buttons in this panel.
                  if (document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur()
                  }
                }}
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-input-border bg-surface-0 text-text-muted transition-colors hover:bg-surface-3 hover:border-input-border-hover hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                title={t('adjustPanel.downloadSourceAudioTooltip')}
                aria-label={t('adjustPanel.downloadSourceAudio')}
                data-testid="timeline-audio-download-button"
              >
                <Download className="w-4 h-4" strokeWidth={2} />
              </button>
            )}
        </div>
        <div className="text-text-muted text-xs font-medium mt-0.5">
          {selectedClipType === 'video' ? t('adjustPanel.videoClip') : selectedClipType === 'audio' ? t('adjustPanel.audioClip') : t('adjustPanel.clip')}
          {' | '}
          {formatDuration(clip.effectiveDuration)}
        </div>
      </section>

      {/* Volume control (audio AND video clips) */}
      {audioControlClip && (
        <section className="min-w-0" data-testid="adjust-panel-audio-section">
          <label className="text-text-secondary text-xs font-medium block mb-2">
            {t('adjustPanel.audio', 'Audio')}
          </label>

          <div className="flex items-center gap-2">
            {/* Mute toggle */}
            <button
              onClick={handleToggleMute}
              className={`
                p-1.5 rounded border transition-colors text-sm
                ${audioControlClip.muted
                  ? 'bg-destructive/10 border-destructive text-destructive'
                  : 'bg-surface-1 border-input-border text-muted-foreground hover:bg-surface-3 hover:border-input-border-hover'
                }
              `}
              title={audioControlClip.muted ? t('adjustPanel.unmute') : t('adjustPanel.mute')}
              data-testid={muteTestId}
            >
              {audioControlClip.muted ? (
                <VolumeX className="w-4 h-4" strokeWidth={2} />
              ) : (
                <Volume2 className="w-4 h-4" strokeWidth={2} />
              )}
            </button>

            {/* Volume slider with a tick mark at 100% (= original volume) */}
            {(() => {
              // Slider operates in position space (0..2). Stored amplitude
              // (0..MAX_VOLUME_AMPLITUDE ≈ 15.85) maps back via amplitudeToPosition.
              const sliderPosition = amplitudeToPosition(audioControlClip.volume)
              const displayPercent = Math.round(sliderPosition * 100)
              return (
                <>
                  <div className="flex-1 relative">
                    <input
                      type="range"
                      min="0"
                      max="2"
                      step="0.01"
                      value={sliderPosition}
                      onChange={handleVolumeChange}
                      className="w-full"
                      disabled={audioControlClip.muted}
                      aria-valuemin={0}
                      aria-valuemax={2}
                      aria-valuenow={sliderPosition}
                      aria-valuetext={`${displayPercent}%`}
                      data-testid={sliderTestId}
                    />
                    {/* 100% tick mark — positioned at the slider midpoint
                        (50% of width) since the range is symmetric 0..2. The
                        pointer-events-none keeps it out of the drag path. */}
                    <div
                      aria-hidden="true"
                      data-testid="timeline-volume-tick-100"
                      className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                    >
                      <div className="h-3 w-px bg-text-muted" />
                    </div>
                  </div>
                  <span className="text-foreground text-sm font-mono min-w-[4ch] text-right">
                    {displayPercent}%
                  </span>
                </>
              )
            })()}
          </div>
          {onAudioFadesChange && <div className="mt-4 space-y-4">
            {(['in', 'out'] satisfies ('in' | 'out')[]).map((side) => <label key={side} className="block text-xs font-medium text-muted-foreground">
              <span className="mb-2 flex justify-between">{t(side === 'in' ? 'adjustPanel.fadeIn' : 'adjustPanel.fadeOut')}
                <output>{(side === 'in' ? audioControlClip.audioFadeInS ?? 0 : audioControlClip.audioFadeOutS ?? 0).toFixed(1)}s</output>
              </span>
              <input type="range" min="0" max={audioControlClip.effectiveDuration} step="0.1" className="w-full accent-primary"
                aria-label={t(side === 'in' ? 'timelineCurves.audioFadeIn' : 'timelineCurves.audioFadeOut', side === 'in' ? 'Audio fade in' : 'Audio fade out')}
                data-testid={`timeline-audio-fade-${side}`} value={side === 'in' ? audioControlClip.audioFadeInS ?? 0 : audioControlClip.audioFadeOutS ?? 0}
                onChange={(event) => onAudioFadesChange(audioControlClip.id,
                  side === 'in' ? Number(event.target.value) : audioControlClip.audioFadeInS ?? 0,
                  side === 'out' ? Number(event.target.value) : audioControlClip.audioFadeOutS ?? 0)} />
            </label>)}
          </div>}
        </section>
      )}

      {audioControlClip && (onExtractAudio || onChangeVoice || onIsolateAudio || onSplitDialogue) && (
        <section className="min-w-0 border-t border-border pt-3" data-testid="adjust-panel-audio-actions">
          <div className="mb-2 text-xs font-medium text-text-secondary">
            {t('adjustPanel.audioActions', 'Audio actions')}
          </div>
          <div className="flex min-w-0 flex-col gap-2">
            {videoOnlyClip && onExtractAudio && (
              <button
                type="button"
                onClick={onExtractAudio}
                disabled={audioActionsBusy || extractAudioDisabled}
                title={extractAudioDisabled ? t('adjustPanel.extractAudioAlreadyExtracted', 'Audio already extracted.') : undefined}
                className="flex h-9 min-w-0 w-full items-center justify-center gap-2 rounded-lg border border-input-border bg-surface-1 px-3 text-sm font-medium text-foreground transition-colors hover:border-input-border-hover hover:bg-surface-3 disabled:cursor-not-allowed disabled:opacity-60"
                data-testid="adjust-panel-extract-audio"
              >
                <Scissors className="h-4 w-4 shrink-0" strokeWidth={2} />
                <span className="min-w-0 truncate">{t('adjustPanel.extractAudio', 'Extract audio')}</span>
              </button>
            )}
            {onChangeVoice && (
              <button
                type="button"
                onClick={openChangeVoiceModal}
                disabled={audioActionsBusy}
                className="flex h-9 min-w-0 w-full items-center justify-center gap-2 rounded-lg border border-input-border bg-surface-1 px-3 text-sm font-medium text-foreground transition-colors hover:border-input-border-hover hover:bg-surface-3 disabled:cursor-wait disabled:opacity-60"
                data-testid="adjust-panel-change-voice"
              >
                <AudioLines className="h-4 w-4 shrink-0" strokeWidth={2} />
                <span className="min-w-0 truncate">
                  {changeVoiceBusy ? t('adjustPanel.processing', 'Processing...') : t('adjustPanel.changeVoice', 'Change voice')}
                </span>
              </button>
            )}
            {onSplitDialogue && (
              <button
                type="button"
                onClick={onSplitDialogue}
                disabled={audioActionsBusy}
                className="flex h-9 min-w-0 w-full items-center justify-center gap-2 rounded-lg border border-input-border bg-surface-1 px-3 text-sm font-medium text-foreground transition-colors hover:border-input-border-hover hover:bg-surface-3 disabled:cursor-wait disabled:opacity-60"
                data-testid="adjust-panel-split-dialogue"
              >
                <Scissors className="h-4 w-4 shrink-0" strokeWidth={2} />
                <span className="min-w-0 truncate">
                  {splitDialogueBusy ? t('adjustPanel.processing', 'Processing...') : t('adjustPanel.splitByDialogue', 'Split by dialogue')}
                </span>
              </button>
            )}
            {onIsolateAudio && (
              <button
                type="button"
                onClick={handleIsolateAudio}
                disabled={audioActionsBusy}
                className="flex h-9 min-w-0 w-full items-center justify-center gap-2 rounded-lg border border-input-border bg-surface-1 px-3 text-sm font-medium text-foreground transition-colors hover:bg-surface-3 hover:border-input-border-hover disabled:cursor-wait disabled:opacity-60"
                data-testid="adjust-panel-isolate-audio"
              >
                <AudioLines className="h-4 w-4 shrink-0" strokeWidth={2} />
                <span className="min-w-0 truncate">
                  {isolateAudioBusy ? t('adjustPanel.processing', 'Processing...') : t('adjustPanel.isolateVocalsBackground', 'Isolate')}
                </span>
              </button>
            )}
          </div>
          {audioActionError && (
            <div className="mt-2 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {audioActionError}
            </div>
          )}
        </section>
      )}

      {/* Speed control */}
      <section className="min-w-0 border-t border-border pt-3" data-testid="adjust-panel-speed-section">
        <label className="text-text-secondary text-xs font-medium block mb-2">
          {t('adjustPanel.speed')}
        </label>

        {/* Speed slider */}
        <div className="flex items-center gap-2">
          <input
            type="range"
            min="0.1"
            max="4.0"
            step="0.1"
            value={currentSpeed}
            onChange={handleSpeedSliderChange}
            className="flex-1"
            data-testid="timeline-speed-slider"
          />
          <span className="text-foreground text-sm font-mono min-w-[4ch] text-right">
            {currentSpeed.toFixed(1)}x
          </span>
        </div>

        {/* Speed presets */}
        <div className="flex gap-1.5 mt-2">
          {SPEED_PRESETS.map((preset) => (
            <button
              key={preset}
              onClick={() => handleSpeedPreset(preset)}
              className={`
                flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors
                ${Math.abs(currentSpeed - preset) < 0.05
                  ? 'bg-primary/10 border-primary text-primary'
                  : 'bg-surface-1 border-input-border text-muted-foreground hover:bg-surface-3 hover:border-input-border-hover'
                }
              `}
            >
              {preset}x
            </button>
          ))}
        </div>
        {videoOnlyClip && onSpeedCurve && <details className="mt-4 border-t border-border pt-3">
          <summary className="cursor-pointer text-sm font-medium text-foreground">
            {t('timelineCurves.title', 'Speed curve')}
          </summary>
          <div className="mt-3">
            <SpeedCurveEditor key={`${clip.id}:${clip.sourcePath}:${clip.trimStart}:${clip.trimEnd}`} clip={videoOnlyClip} onApply={onSpeedCurve} busy={timeEditBusy} error={timeEditError} />
          </div>
        </details>}
      </section>

      {videoOnlyClip && onClipVisualsChange && (
        <section className="min-w-0 border-t border-border pt-3" data-testid="adjust-panel-video-section">
          <details>
            <summary className="cursor-pointer text-sm font-medium text-foreground">
              {t('adjustPanel.videoControls', 'Video controls')}
            </summary>
            <div className="mt-3">
              <BasicClipControls
                clip={videoOnlyClip}
                onChange={onClipVisualsChange}
                onTimeEdit={onTimeEdit}
                timeEditBusy={timeEditBusy}
                timeEditError={timeEditError}
                playhead={playheadPosition}
              />
            </div>
          </details>
        </section>
      )}

      {/* Fade-in control (video clips only) */}
      {videoOnlyClip && (
        (() => {
          const maxFade = Math.max(0, videoOnlyClip.effectiveDuration)
          const currentFade = Math.max(0, Math.min(maxFade, videoOnlyClip.fadeInS ?? 0))
          return (
            <section className="min-w-0" data-testid="timeline-clip-fade-in-section">
              <label className="text-text-secondary text-xs font-medium block mb-2">
                {t('adjustPanel.fadeIn')}
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="range"
                  min={0}
                  max={maxFade}
                  step={0.1}
                  value={currentFade}
                  onChange={handleFadeInChange}
                  className="flex-1"
                  data-testid="timeline-clip-fade-in-slider"
                  aria-label={t('adjustPanel.fadeInDuration')}
                />
                <span
                  className="text-foreground text-sm font-mono min-w-[4ch] text-right"
                  data-testid="timeline-clip-fade-in-value"
                >
                  {currentFade.toFixed(1)}s
                </span>
              </div>
              <div className="text-text-muted text-xxs mt-1">
                {t('adjustPanel.fadeInHint', { duration: currentFade.toFixed(1) })}
              </div>
            </section>
          )
        })()
      )}

      {/* Fade-out control (video clips only) */}
      {videoOnlyClip && (
        (() => {
          const maxFade = Math.max(0, videoOnlyClip.effectiveDuration)
          const currentFade = Math.max(0, Math.min(maxFade, videoOnlyClip.fadeOutS ?? 0))
          return (
            <section className="min-w-0" data-testid="timeline-clip-fade-out-section">
              <label className="text-text-secondary text-xs font-medium block mb-2">
                {t('adjustPanel.fadeOut')}
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="range"
                  min={0}
                  max={maxFade}
                  step={0.1}
                  value={currentFade}
                  onChange={handleFadeOutChange}
                  className="flex-1"
                  data-testid="timeline-clip-fade-out-slider"
                  aria-label={t('adjustPanel.fadeOutDuration')}
                />
                <span
                  className="text-foreground text-sm font-mono min-w-[4ch] text-right"
                  data-testid="timeline-clip-fade-out-value"
                >
                  {currentFade.toFixed(1)}s
                </span>
              </div>
              <div className="text-text-muted text-xxs mt-1">
                {t('adjustPanel.fadeOutHint', { duration: currentFade.toFixed(1) })}
              </div>
            </section>
          )
        })()
      )}

      {/* Additional clip details */}
      <section className="mt-auto min-w-0 border-t border-border pt-3">
        <details>
          <summary className="cursor-pointer text-sm font-medium text-foreground">
            {t('adjustPanel.details', 'Details')}
          </summary>
          <div className="mt-3 space-y-1 text-xxs text-text-muted">
            <div className="flex justify-between gap-2">
              <span>{t('adjustPanel.originalDuration')}</span>
              <span>{formatDuration(clip.originalDuration)}</span>
            </div>
            <div className="flex justify-between gap-2">
              <span>{t('adjustPanel.trimStart')}</span>
              <span>{clip.trimStart.toFixed(2)}s</span>
            </div>
            <div className="flex justify-between gap-2">
              <span>{t('adjustPanel.trimEnd')}</span>
              <span>{clip.trimEnd.toFixed(2)}s</span>
            </div>
            {selectedClipType === 'audio' && selectedAudioClip && (
              <div className="flex justify-between gap-2">
                <span>{t('adjustPanel.startOffset')}</span>
                <span>{selectedAudioClip.startOffset.toFixed(2)}s</span>
              </div>
            )}
            {audioFitWindowS !== null && selectedAudioClip && (
              <div
                className="flex justify-between gap-2"
                data-testid="adjust-panel-audio-fit-window"
              >
                <span>{t('adjustPanel.fitWindow')}</span>
                <span className="truncate text-right">{audioFitWindowRange}</span>
              </div>
            )}
            {audioFitWindowS !== null && selectedAudioClip && selectedAudioClip.effectiveDuration > audioFitWindowS && (
              <div
                className="rounded border border-warning/30 bg-warning/5 px-2 py-1 text-warning"
                data-testid="adjust-panel-audio-fit-warning"
              >
                {t('adjustPanel.fitWindowTooLong')}
              </div>
            )}
          </div>
        </details>
      </section>
      </div>
    </div>
  )
})

function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`
  }
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}
